/// <reference types="@types/googlemaps" />
import React, { useEffect, useRef, useState, useContext } from 'react';
import { IVehicleSearch } from '../../interfaces/VehicleSearch';
import { isUnderTwentyOne, priceFormat } from "../MakeReservation/util";
import { translateService } from "../../services/TranslateService";
import { basePrice } from "../MakeReservation/pricingCalculations";
import { DataContext } from "../../contexts/dataContext";
import { Backdrop, Button } from "@mui/material";
import CalendarComponent from "../VehicleSearch/reservedTable";
import axios from "axios";
import moment from "moment";
import { InfoWindowSingleton } from './InfoWindowSingleton';
import {VipCodeFromMap, checkVipCodeValid} from "../VehicleSearch/VipCodeVehicles";
import Modal from "react-modal";

interface googleMapProps {
    lat: number,
    lng: number,
    zoom: number,
    mapData: IVehicleSearch[],
    width: string,
}

interface IiconMapping {
    [key: string]: string;
}

declare var google: any;
declare global {
    interface Window {
        google: any;
    }
}
interface IMarker {
    vehicleId: number;
    marker: google.maps.marker.AdvancedMarkerElement;
}

interface handleMarkerClickProps {
    marker: google.maps.marker.AdvancedMarkerElement,
    vehicle: IVehicleSearch,
    position: google.maps.LatLngLiteral,
    total: number,
    index: number
}

const iconMapping: IiconMapping = {
    compact: 'car',
    stw: 'car-large',
    suv: 'car-large',
    minibus: 'minibus',
    van: 'van',
    default: 'car',
    vip: 'vip'
};

const customStyles = {
    content: {
        width: '60%',
        top: '55%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0,0,0,0.7)',
        color: 'white',
    },
    overlay: {
        zIndex: '20',
    },
};

// Documentations for Google Maps API: https://developers.google.com/maps/documentation/javascript/tutorial
const GoogleMap = ({ lat, lng, zoom, mapData, width }: googleMapProps) => {
    const mapId = "GOOGLE_MAPS_RRM_ID";
    const mapRef = useRef(null);
    const dataContext = useContext(DataContext);
    const [markers, setMarkers] = useState<IMarker[]>([]);
    const [offsetMarkers, setOffsetMarkers] = useState<IMarker[]>([]);
    const [vehicleGroups, setVehicleGroups] = useState<IVehicleSearch[][]>([]);
    const [googleMap, setGoogleMap] = useState<google.maps.Map | null>(null);
    const [greenMarker, setGreenMarker] = useState<google.maps.marker.AdvancedMarkerElement | null>(null);
    const [reservationTable, setReservationTable] = useState<boolean>(false);
    const [reservedSlots, setReservedSlots] = React.useState<any>()
    const [drawLine, setDrawLine] = useState<google.maps.Polyline[]>([]);
    const [selectedVipCar, setSelectedVipCar] = useState<IVehicleSearch | undefined>();
    const [vipCodeCheckOpen, setVipCodeCheckOpen] = useState<boolean>(false);
    const [markersLoading, setMarkersLoading] = useState<boolean>(false)
    const [mapMarkerData, setMapMarkerData] = useState<IVehicleSearch[]>([]);
    const [oneWayEnabled, setOneWayEnabled] = useState<boolean>(false);
    const [onewayReturnMarker, setOnewayReturnMarker] = useState<google.maps.marker.AdvancedMarkerElement | null>(null);
    Modal.setAppElement('body');

    const fetchMapData = async () => {
        try {
            const mapData= sessionStorage.getItem('allCars');
            if (mapData) {
                setMapMarkerData(JSON.parse(mapData));
            } else {
                setMapMarkerData([]);
                clearMarkers();
            }
        } catch (e) {
            console.log(e)
        }
    }

    const fetchOneWayEnabled = async () => {
        try {
            const oneWayRes = sessionStorage.getItem('oneway');
            oneWayRes && setOneWayEnabled(JSON.parse(oneWayRes));
        } catch (e) {
            console.log(e);
        }
    }

    /**
     * @description useEffect to fetch one way enabled
     * @returns fetchOneWayEnabled
     */

    useEffect(() => {
        if(typeof dataContext.oneWayRes === 'undefined' || dataContext.oneWayRes === null){
            fetchOneWayEnabled();
        } else {
            setOneWayEnabled(dataContext.oneWayRes);
        }
    }, [dataContext.oneWayRes]);

    /**
     * @description useEffect to fetch map data
     * @returns fetchMapData
     */
    useEffect(() => {
        if(!dataContext.showDefaultResults && dataContext.mapCanInit){
            if(dataContext.mapData && dataContext.mapData.length > 0){
                setMapMarkerData(dataContext.mapData);
            } else {
                fetchMapData();
            }
        }
    }, [dataContext.mapData, dataContext.mapCanInit, dataContext.showDefaultResults, oneWayEnabled, dataContext.searchParameters]);

    const currentTime = moment();
    const isNightTime = currentTime.hour() >= 21 || currentTime.hour() < 7;

    /**
     * @description this handles the selectVipVehicle modal popup
     * @param vehicle
     */
    const selectVipVehicle = (vehicle: IVehicleSearch) => {
        setSelectedVipCar(vehicle);
        setVipCodeCheckOpen(true);
    }

    /**
     * @description Function to get the icon type based on the vehicle type in acriss and availability
     * @param data
     */
    const getIconType = (data: IVehicleSearch): string => {
        let iconType: string = 'default';
        let icon: string = data.vehicleModelData.modelacriss;
        let van: boolean = data.vehicleModelData.type!.includes('van');
        let cloudTag: string[] = data.tagCloud;
        if(data.privateData?.vip && data.availabilityData?.available){
            // need to add new icon for unavailable vip vehicle
            iconType = 'vip'
        }
        else {
            if (dataContext.acrissCodeSet?.compact.includes(icon) && !van) {
                iconType = 'compact';
            } else if (dataContext.acrissCodeSet?.stw.includes(icon)) {
                iconType = 'stw';
            } else if (dataContext.acrissCodeSet?.suv.includes(icon)) {
                iconType = 'suv';
            } else if (dataContext.acrissCodeSet?.minibus.includes(icon) || cloudTag.includes('minibussi')) {
                iconType = 'minibus';
            } else if (van) {
                iconType = 'van';
            }
        }

        return iconMapping[iconType];
    };

    /**
     * @description Function to get the icon URL based on the vehicle type
     * @param data
     * @param multi
     */

    const getIconUrl = (data: IVehicleSearch, multi?: boolean, returnLocIcon?: boolean): string => {
        if (!data) {
            return '';
        }

        let iconTxt = getIconType(data);
        if (!data.availabilityData?.available) {
           iconTxt += '-unavailable';
        } else if (!multi) {
            let isVehicleInFilteredSet = false;
            dataContext.filteredVehicles?.forEach((filteredVehicle) => {
                if (isVehicleInFilteredSet) return;
                if(filteredVehicle.id === data.id) {
                    isVehicleInFilteredSet = true;
                }
            })

            if(!isVehicleInFilteredSet){
                // if car/van not included in filter setting results and available -->
                // show gray icon to make easier to see
                if(iconTxt.includes('car')) {
                    iconTxt = "car-grey"
                } else if (iconTxt.includes('van')){
                    iconTxt = "van-grey";
                }
            }
        }

        if (returnLocIcon) {
            // gray icons also used to show return locations for one-way vehicles
            if(iconTxt.includes('car')) {
                iconTxt = "car-grey"
            } else { // #TODO: Get gray icons for minibus (if oneway will ever include these types)
                iconTxt = "van-grey";
            }
        } else if (multi) {
            iconTxt = 'multiple-2';
        }

        return `${dataContext.rootUrl}/assets/map-pin-vehicle-icons/map-pin-icon-${iconTxt}.png`;
    };

    /**
     * @description Function to initialize the map
     */

    const initialize = async () => {
        if(googleMap){
            return;
        }

        const script: HTMLScriptElement = document.createElement(`script`);
        script.id = "google-maps-script";
        document.body.appendChild(script);
        if (mapRef.current) {
            const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;

            const constantDefaultMapZoomLevel: number = 14;
            const constantMinMapZoomLevel: number = 12;
            const constantMaxMapZoomLevel: number = 20;

            setGoogleMap(new Map(mapRef.current, {
                center: { lat, lng },
                zoom: constantDefaultMapZoomLevel,
                minZoom: constantMinMapZoomLevel,
                maxZoom: constantMaxMapZoomLevel,
                mapId: mapId,
                mapTypeId: google.maps.MapTypeId.ROADMAP,
                mapTypeControl: false,
                fullscreenControl: false,
                streetViewControl: false,
                gestureHandling: 'greedy',
                zoomControl: true,
                zoomControlOptions: {
                    position: google.maps.ControlPosition.LEFT_TOP
                },
                clickableIcons: false,
                styles: [{
                    featureType: 'poi',
                    elementType: 'labels',
                    stylers: [{
                        visibility: 'off'
                    }]
                }],
            }));
        }
    };

    /**
     * @description Remove JS script
     */
    const removeScript = () => {
        const script: HTMLElement | null = document.getElementById("google-maps-script");
        if (script) {
            script.remove();
        }
    }

    /**
     * @description useEffect to initialize the map
     * @returns removeScript
     */

    useEffect(() => {
        if (dataContext.showDefaultResults || !dataContext.mapCanInit ) return;

        initialize();
        return () => {
            removeScript(); // Remove the script when the component is unmounted
        }
    }, [dataContext.showDefaultResults, dataContext.mapCanInit]);


    /**
     * @description useEffect to animate the center change of the map
     * @param lat
     * @param lng
     */

    useEffect(() => {
        if(googleMap){
            googleMap.setCenter({ lat, lng });
            addGreenMarker({ lat, lng });
        }

    }, [lat, lng, dataContext.searchParameters]);

    /**
     * @description Function to clear map markers
     * @returns markers
     */

    const clearMarkers = () => {
        InfoWindowSingleton.getInstance().close();

        if(markers && markers.length > 0){
            markers.forEach((marker: IMarker) => marker.marker.map = null);
            setMarkers([]);
            setVehicleGroups([]);
        }

        if(offsetMarkers && offsetMarkers.length > 0){
            offsetMarkers.forEach((marker: IMarker) => marker.marker.map = null);
            setOffsetMarkers([]);
        }

        if(drawLine){
            drawLine.forEach((line: google.maps.Polyline) => line.setMap(null));
            setDrawLine([]);
        }

        if(onewayReturnMarker){
            // clear one-way return marker if one exists
            onewayReturnMarker.remove();
            onewayReturnMarker.map = null;
            setOnewayReturnMarker(null);
        }

        setMarkersLoading(true);
    }

    const handleMarkerPopulate = (mapData: IVehicleSearch[]) => {
        if(!dataContext.showDefaultResults && (mapData && mapData.length > 0) && !markersLoading){
            if(markers && markers.length >= 1) { // If there are markers on the map
                clearMarkers();
            }
            setMarkersLoading(true);
        }
        return;
    }

    useEffect(() => {
        handleMarkerPopulate(mapMarkerData);
    }, [mapMarkerData, oneWayEnabled, dataContext.showDefaultResults, dataContext.vehicle, dataContext.filteredVehicles]);


    /**
     * @description Function to handle the date picker
     * @param id
     */

    const handleDatePicker = (id?: number) => {
        if (id) {
            reservationArr(id);
        }
        setReservationTable(true);
        if (reservationTable) {
            setReservationTable(false);
        }
    }

    /**
     * @description Function to select a vehicle
     * @param vehicle
     */

    const selectVehicle = (vehicle: IVehicleSearch)=> {
        if (vehicle.availabilityData?.available) {
            if (vehicle.id !== dataContext.vehicle?.id) {
                sessionStorage.removeItem("customer");
                sessionStorage.removeItem("services");
            }
            if (dataContext.customer && dataContext.vehicle) {
                if (dataContext.customer.dateOfBirth !== '' || dataContext.customer.personalIdCode !== '') {
                    if (isUnderTwentyOne(dataContext.customer.dateOfBirth, dataContext.customer.personalIdCode) && vehicle) {
                        if (vehicle.vehicleData.noundertwentyone) {
                            dataContext.changeTwentyModal(true);
                            return;
                        }
                    }
                }
            }
            dataContext.changeVehicle(vehicle);
            dataContext.changePage(1)
            dataContext.changeMapData([vehicle]); // Set mapData to only include the selected vehicle
            dataContext.changeSearchParameters(dataContext.searchParameters);

            if (dataContext.user) {
                sessionStorage.setItem("user", JSON.stringify(dataContext.user));
            }
            dataContext.changeRoute("/makereservation");
            if (!dataContext.oneWayRes) {
                dataContext.sendGaEvent('reservation', 'beginReservationFromMaps', vehicle.id, 0);
            } else {
                dataContext.sendGaEvent('reservation', 'beginOneWayReservationFromMaps', vehicle.id, 0);
            }
        } else {
            handleDatePicker(vehicle.id);
            if (!dataContext.oneWayRes) {
                dataContext.sendGaEvent('searchCars', 'openCalendarFromMaps', '', 0);
            } else {
                dataContext.sendGaEvent('searchCars', 'openOneWayCalendarFromMaps', '', 0);
            }
        }
    }

    /**
     * @description Function to get the reserved slots for a vehicle
     * @param vehicleId
     */

    const reservationArr = async (vehicleId: number) => {
        // parameters axios get
        const start = moment(dataContext.searchParameters?.startDate).format('YYYY-MM-DD')
        const end = moment(dataContext.searchParameters?.endDate).format('YYYY-MM-DD')

        const params = {
            startDate: start,
            startTime: dataContext.searchParameters?.startTime,
            endDate: end,
            endTime: dataContext.searchParameters?.endTime,
        }

        try {
            // axios get
            if (vehicleId && params) {
                const response = await axios.get(`${dataContext.v3ApiUrl}/anonymous/vehicles/${vehicleId}/free-intervals`, { params })
                setReservedSlots(response.data.data)
            }
        } catch (e) {
            console.log(e)
        }
    }

    useEffect(() => {
        // show one-way vehicle's return location marker on map
        if(googleMap && dataContext.showOneWayReturnLocation && oneWayEnabled) {
            let oneWayVehicleData;
            mapMarkerData.forEach((vehicleMarker: IVehicleSearch) => {
                if (vehicleMarker.id === dataContext.showOneWayReturnLocation) {
                    oneWayVehicleData = vehicleMarker;
                }
            })

            if (oneWayVehicleData) {
                addOnewayReturnMarker(oneWayVehicleData).
                finally(dataContext.changeShowOneWayReturnLocation(undefined));
            } else {
                dataContext.changeShowOneWayReturnLocation(undefined);
            }
        }
    }, [dataContext.showOneWayReturnLocation])

    /**
     * @description Function to group the vehicles by locations
     * @returns Array
     */
    const groupVehiclesByLocation = (vehicles: IVehicleSearch[]): IVehicleSearch[][] => {
        const locationMap = new Map<string, IVehicleSearch[]>();

        vehicles.forEach((vehicle: IVehicleSearch) => {
                const key: string = `${vehicle.homeLocationData.coordinates.latitude.toFixed(12)},${vehicle.homeLocationData.coordinates.longitude.toFixed(12)}`;
                const group: IVehicleSearch[] | undefined = locationMap.get(key);

                if (group) {
                    group.push(vehicle);
                } else {
                    locationMap.set(key, [vehicle]);
                }
        });

        return Array.from(locationMap.values());
    };

    /**
     * @description Function to add a green marker (center location search Icon) on the map
     * @param position
     */
    const addGreenMarker = async (position: google.maps.LatLngLiteral) => { // Remove old green marker from the map
        if(greenMarker){
            greenMarker.remove();
            greenMarker.map = null;
            setGreenMarker(null);
        }

        const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;
        if (greenMarker) {
            greenMarker.remove();
        }
        const icon: HTMLImageElement = document.createElement('img');
        const greenLocationMarker: string = `${dataContext.rootUrl}/assets/icons/new/map-icon-locate.png`;
        icon.src = greenLocationMarker;
        const marker: google.maps.marker.AdvancedMarkerElement = new AdvancedMarkerElement({
            position,
            map: googleMap,
            content: icon,
            zIndex: 1
        });
        setGreenMarker(marker);
        return marker;
    }

    const addOnewayReturnMarker = async (vehicle: IVehicleSearch) => { // Remove old green marker from the map
        if(onewayReturnMarker){
            onewayReturnMarker.remove();
            onewayReturnMarker.map = null;
            setOnewayReturnMarker(null);
        }

        if (!vehicle.returnLocationData?.coordinates.latitude || !vehicle.returnLocationData?.coordinates.longitude || !googleMap || !oneWayEnabled) return;

        InfoWindowSingleton.getInstance().close(); // close any other info windows that are open

        const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

        const icon: HTMLImageElement = document.createElement('img');
        icon.src = getIconUrl(vehicle, false, true);

        const position: google.maps.LatLngLiteral = {lat: Number(vehicle.returnLocationData.coordinates.latitude.toFixed(12)),
            lng: Number(vehicle.returnLocationData.coordinates.longitude.toFixed(12))}
        const marker: google.maps.marker.AdvancedMarkerElement = new AdvancedMarkerElement({
            position,
            map: googleMap,
            content: icon,
            gmpClickable: true,
            zIndex: 1,
        });

        marker.addListener('gmp-click', () => {
            const contentString: string = getInfoWindowContent(vehicle, true);
            InfoWindowSingleton.getInstance().setContent(contentString);
            // @ts-ignore
            InfoWindowSingleton.getInstance().open(googleMap, marker);
        })

        setOnewayReturnMarker(marker);
        googleMap?.panTo(position);
    }

    /**
     * @description Function to get the info window content
     * @param vehicle
     */

    const getInfoWindowContent = (vehicle: IVehicleSearch, returnLocation?: boolean) => {
        const price: number = basePrice(vehicle);
        const isVipCodeValid = checkVipCodeValid(vehicle.id, vehicle.privateData?.private, dataContext.validatedVipVehicles);
        return `<div style="text-align: center">
                    <img  width="150" height="100" src="https://vehicles-cdn.24rent.fi/${vehicle.id}_medium.jpeg" style="width: 150px; height: 100px; object-fit: cover; border-radius: 10%; " loading="lazy"/>
                    <h2 style="text-align: center; padding-top: 5px; margin-bottom: 4px">${vehicle.vehicleModelData.manufacturer} ${vehicle.vehicleModelData.name} ${vehicle.vehicleModelData.acriss ?? ''} ${vehicle.vehicleModelData.dimensions.trunkVolume !== 0 ? vehicle.vehicleModelData.dimensions.trunkVolume + 'm³' : ''} </h2>  
                    ${vehicle.availabilityData!.available ? `<p class="adress" style="font-weight: lighter; font-size: 13px; text-align: center; margin-bottom: 4px">${vehicle.homeLocationData.address}</p>` : ''}
                    ${vehicle.availabilityData!.available ? `<div class="price" style="background-color: #0BB395; border-radius: 5px; padding: 5px"><p style="text-align: center; color: white; font-weight: bold; margin-bottom: unset"> ${priceFormat(price)} €</p></div>` : ''}
                    ${!vehicle.availabilityData!.available ? `<label style="color: red; font-weight: bold; margin: unset; display: flex; justify-content: center"> ${translateService.translate('vehicleSearch1.Reserved')}</label>` : ''}
                   ${!returnLocation ?  `<button id="selectCar-${vehicle.id}" style="background-color: #FBB022; border-radius: 5px; padding: 5px; color: white; font-weight: bold; width: 100%; margin-top: 5px; border: none; text-transform: uppercase;" >
            ${vehicle.availabilityData!.available ? (!vehicle.privateData?.private || isVipCodeValid) ?
                translateService.translate('vehicleSearch.selectCar')
                : translateService.translate('vehicleSearch1.privateSelection')
            : translateService.translate('vehicleSearch1.reservedSearch')}
            </button>` : 
            `<button style="background-color: #FBB022; border-radius: 5px; padding: 5px; color: white; font-weight: bold; width: 100%; margin-top: 5px; border: none; text-transform: uppercase;" >
            ${translateService.translate('vehicleSearch1.returnLocation')}
            </button>`}
             </div>`
    }

    /**
     * @description Function to create an offset marker
     * @param position
     * @param vehicle
     * @param index
     * @param total
     */
    const creatOffSetMarker = (position: google.maps.LatLngLiteral, vehicle: IVehicleSearch, index: number, total: number) => {
        const radius: number = 0.0001;
        const lineRadius: number = radius * 1; // 95% of the original radius
        const divisor: number = total === 2 ? 2 : total; // If there are only two vehicles, divide the circle into three parts
        const angleStep: number = (2 * Math.PI) / divisor; // Angle between each vehicle
        const angle: number = angleStep * index;

        // Adjust lineRadius based on the angle
        let adjustedLineRadius: number;
        if (angle > Math.PI * 0.75 && angle < Math.PI * 1.25) {
            // Bottom of the circle - make the line shorter
            adjustedLineRadius = radius * 0.85; // 85% of the original radius
        } else if ((angle > Math.PI * 1.75 && angle < Math.PI * 2) || (angle > 0 && angle < Math.PI * 0.25)) {
            // Top of the circle - make the line longer
            adjustedLineRadius = radius * 0.99; // 95% of the original radius
        } else {
            // For the rest of the circle, use the original lineRadius
            adjustedLineRadius = lineRadius;
        }

        // Calculate the position of the offset markers
        const offsetPosition: { lat: number, lng: number } = {
            lat: position.lat + radius * Math.cos(angle),
            lng: position.lng + radius * Math.sin(angle)
        };

        const offsetIconUrl: string = getIconUrl(vehicle);
        const offsetIcon: HTMLImageElement = document.createElement('img');
        offsetIcon.id = `${vehicle.id}`;
        offsetIcon.src = offsetIconUrl;

        // Create the offset marker
        const offsetMarker: google.maps.marker.AdvancedMarkerElement = new google.maps.marker.AdvancedMarkerElement({
            position: offsetPosition,
            map: googleMap,
            content: offsetIcon,
            gmpClickable: true,
            gmpDraggable: false,
        });


        // Create a line from the central point to the offset marker
        const line = new google.maps.Polyline({
            path: [position, offsetPosition],
            geodesic: true,
            strokeColor: '#000000',
            strokeOpacity: 1.0,
            strokeWeight: 2,
            map: googleMap,
        });
        // Add the line to the drawLine state
        setDrawLine(prevLines => [...prevLines, line]);
        // Add the offset marker to the offsetMarkers state
        setOffsetMarkers(prevMarkers => [...prevMarkers, { vehicleId: vehicle.id, marker: offsetMarker }]);

        offsetMarker.addListener('gmp-click', () => {
            const contentString: string = getInfoWindowContent(vehicle);
            google.maps.event.addListener(InfoWindowSingleton.getInstance(), 'domready', () => {
                const selectCarButton = document.getElementById('selectCar-' + vehicle.id);
                if (selectCarButton) {
                    selectCarButton.addEventListener('click', () => {
                        if(vehicle.privateData?.private &&
                            !checkVipCodeValid(vehicle.id, vehicle.privateData?.private, dataContext.validatedVipVehicles)){
                            selectVipVehicle(vehicle);
                        } else {
                            selectVehicle(vehicle);
                        }
                    });
                }
            });
            InfoWindowSingleton.getInstance().setContent(contentString);
            // @ts-ignore
            InfoWindowSingleton.getInstance().open(googleMap, offsetMarker);
        })
    }

    /**
     * @description Function to handle the marker click event
     * @param marker
     * @param vehicle
     * @param total
     * @param index
     * @param position
     */

    const handleMarkerClick = ({marker, vehicle, total, index, position}: handleMarkerClickProps ) => {
            const contentString: string = getInfoWindowContent(vehicle);
            google.maps.event.addListener(InfoWindowSingleton.getInstance(), 'domready', () => {
                const selectCarButton = document.getElementById('selectCar-' + vehicle.id);
                if (selectCarButton) {
                    selectCarButton.addEventListener('click', () => {
                        if(vehicle.privateData?.private &&
                            !checkVipCodeValid(vehicle.id, vehicle.privateData?.private, dataContext.validatedVipVehicles)){
                            selectVipVehicle(vehicle);
                        } else {
                            selectVehicle(vehicle);
                        }
                    });
                }
            });
            // Close the current infoWindow if it exists
            InfoWindowSingleton.getInstance().close();

            InfoWindowSingleton.getInstance().setContent(contentString);
            // @ts-ignore
            InfoWindowSingleton.getInstance().open(googleMap, marker);
    };


    /** @description Function to create a marker on the map
     * @param vehicle
     * @param group
     * @param index
     * @param total
     */
    const createMarker = async (vehicle: IVehicleSearch, group: IVehicleSearch[], index: number, total: number): Promise<IMarker> => {
        const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary;

        const position = { lat: vehicle.homeLocationData.coordinates.latitude,
                lng: vehicle.homeLocationData.coordinates.longitude }

        let iconUrl: string = getIconUrl(vehicle, total > 1); // If there are multiple vehicles, use the multiple icon
        let icon: HTMLImageElement = document.createElement('img');
        icon.src = iconUrl;
        icon.id = `${vehicle.id}`;

        // this is either the three dot marker or a single vehicle marker
        const marker: google.maps.marker.AdvancedMarkerElement = new AdvancedMarkerElement({
            position,
            map: googleMap,
            content: icon,
            gmpClickable: true,
            gmpDraggable: false,
            zIndex: 10
        });

       if (googleMap) {
        marker.addListener('gmp-click', () => {
            if(total > 1) {
                // Zoom map to the location of the group of vehicles
                googleMap.setCenter(position);

                // Define the final zoom level and the zoom increment
                const finalZoom = 22;
                const zoomIncrement = 0.5;

                // Start the zoom animation
                let currentZoom = googleMap.getZoom();
                const zoomInterval = setInterval(() => {
                    if (currentZoom >= finalZoom) {
                        clearInterval(zoomInterval);
                    } else {
                        currentZoom += zoomIncrement;
                        googleMap.setZoom(currentZoom);
                    }
                }, 100); // Adjust the interval time to control the speed of the zoom animation

                group.forEach((vehicle: IVehicleSearch, groupIndex: number) => {
                    creatOffSetMarker(position, vehicle, groupIndex, total);
                });
            } else {
                handleMarkerClick({marker, vehicle, total, index, position});
            }

            // Close the infoWindow when the map is clicked
            googleMap.addListener('click', () => {
                InfoWindowSingleton.getInstance().close();
            });
        });
        }
        return {
            vehicleId: vehicle.id,
            marker
        }
    };

    /**
     * @description Function to create promise markers and group the vehicles by location
     * @param vehicleGroups
     */
    const createPromiseMarkers = (vehicleGroups: IVehicleSearch[][]) => {
        const promiseMarkers: Promise<IMarker>[] = [];
            vehicleGroups.forEach((group: IVehicleSearch[], index: number) => {
                group.forEach((vehicle: IVehicleSearch, groupIndex: number) => {
                    promiseMarkers.push(createMarker(vehicle,group ,groupIndex, group.length));
                });
            });

        return promiseMarkers;
    }

    /**
     * @description Function to create markers and set them on the map
     * @param mapData
     */

    const createMarkersAndSetOnMap = async (mapData: IVehicleSearch[]) => {
        if (!mapData){
            throw new Error("No map data provided");
        }

        const vehicleGroups: IVehicleSearch[][] = groupVehiclesByLocation(mapData);
        setVehicleGroups(vehicleGroups);

        const promiseMarkers: Promise<IMarker>[] = createPromiseMarkers(vehicleGroups);

        try {
            const newMarkers: IMarker[] = await Promise.all(promiseMarkers);
            const mappedMarkers: IMarker[] = newMarkers.map((marker: IMarker, index: number) => ({ vehicleId: marker.vehicleId, marker: marker.marker}));
            setMarkers(mappedMarkers);
            dataContext.changeMapMarkers(newMarkers);
        } catch (error) {
            console.error("Error creating markers: ", error);
        } finally {
            googleMap!.setZoom(10);
        }
    }

    useEffect(() => {
        if (!googleMap) { // If Google Map is not loaded, return
            return;
        }

        // prevent marker creation if there are markers already on the map
        if(markers && markers.length > 0){
            return;
        }

        if (markersLoading) { // If markers are loading, return
            if(mapMarkerData && mapMarkerData.length > 0 && !dataContext.vehicle){
                createMarkersAndSetOnMap(mapMarkerData);
            } else if(dataContext.vehicle){
                createMarkersAndSetOnMap([dataContext.vehicle]);
            }

            setMarkersLoading(false);

            return;
        }
    }, [markersLoading, oneWayEnabled]);

    return (<>
        <div className="stickyDiv">
            <Modal isOpen={reservationTable} style={customStyles}>
                <div>
                    <div className={'row'} style={{width: 'inherit', marginTop: "5px", height: '100%'}}>
                        <div className={'col-6'}></div>
                        <div className={'col-6 d-flex justify-content-end mb-3'}>
                            <button className='btn btn-sm btn-primary'
                                onClick={() => {handleDatePicker()}}> X </button>
                        </div>
                    </div>
                    <CalendarComponent freeSlots={reservedSlots}/>
                </div>
            </Modal>
            <div className="position-relative">
                 <Backdrop
                    sx={{ color: '#fbb040', zIndex: (theme) => theme.zIndex.drawer + 1005, position: "absolute", backdropFilter: isNightTime ? "blur(5px)" : "none" }}
                    open={((!dataContext.oneWayRes && dataContext.showDefaultResults) || (isNightTime && !dataContext.customer?.loggedIn)) ?? false}>
                    {isNightTime && !dataContext.showDefaultResults ?
                        <button id="mapLoginBtn" className={'btn btn-primary btn-outline-primary font-weight-bold text-uppercase'}
                            style={{
                                display: "flex",
                                minWidth: "20%",
                                minHeight: 60,
                                maxHeight: 120,
                                fontSize: 25,
                                lineHeight: '42px',
                                textAlign: "center",
                                justifyContent: "center",
                                position: "absolute",
                                top: "20%"
                            }}
                            onClick={() => {
                                document.getElementById("searchbox-label")?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center'
                                });
                            }}>
                            {translateService.translate('vehicleSearch1.afterHourLogin')} </button> :
                        <button className={'btn btn-primary btn-outline-primary font-weight-bold text-uppercase'}
                            style={{
                                display: "flex",
                                minWidth: "20%",
                                minHeight: 60,
                                maxHeight: 120,
                                fontSize: 25,
                                lineHeight: '42px',
                                textAlign: "center",
                                justifyContent: "center",
                                position: "absolute",
                                top: "20%"
                            }}
                            onClick={() => {
                                document.getElementById("searchbox-label")?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center'
                                });
                                (document.querySelector('.searchbox-input') as HTMLElement)?.focus();
                            }}>
                            {translateService.translate('vehicleSearch1.selectLocationAndDate')} </button>
                    }
                </Backdrop>
                {selectedVipCar ? <VipCodeFromMap vehicle={selectedVipCar} selectVehicle={selectVehicle}
                                                  vipCodeCheckOpen={vipCodeCheckOpen}
                                                  setVipCodeCheckOpen={setVipCodeCheckOpen} clearSelectedVehicle={setSelectedVipCar}/> : null}
                { dataContext.showDefaultResults || !dataContext.mapCanInit?
                    <div className="default-map-image"></div>
                    : <div ref={mapRef} style={{ width: width ?? "100%", height: "100vh" }} />
                }
            </div>
        </div>
    </>);
}

export default GoogleMap;